/**
 * Keycloak OIDC authentication (Authorization Code flow with PKCE).
 * Public client `dwp-portal` in the `dwp` realm - no client secret needed.
 */

const KEYCLOAK_URL = 'https://auth.zentrydigitalmm.com'
const REALM = 'dwp'
const CLIENT_ID = 'dwp-portal'

const AUTH_URL = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/auth`
const TOKEN_URL = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/token`
const LOGOUT_URL = `${KEYCLOAK_URL}/realms/${REALM}/protocol/openid-connect/logout`

const LS_ACCESS = 'dwp_access_token'
const LS_REFRESH = 'dwp_refresh_token'
const LS_ID = 'dwp_id_token'
const LS_EXPIRES = 'dwp_expires_at'
const SS_VERIFIER = 'dwp_pkce_verifier'
const SS_STATE = 'dwp_oidc_state'

export interface User {
  name: string
  username: string
  email: string
  roles: string[]
  initials: string
}

// ---------- helpers ----------

function base64UrlEncode(bytes: Uint8Array): string {
  let str = ''
  bytes.forEach((b) => (str += String.fromCharCode(b)))
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomString(byteLength = 48): string {
  return base64UrlEncode(crypto.getRandomValues(new Uint8Array(byteLength)))
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64UrlEncode(new Uint8Array(digest))
}

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split('.')[1]
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

function redirectUri(): string {
  return `${window.location.origin}/callback`
}

function storeTokens(data: {
  access_token: string
  refresh_token?: string
  id_token?: string
  expires_in?: number
}) {
  localStorage.setItem(LS_ACCESS, data.access_token)
  if (data.refresh_token) localStorage.setItem(LS_REFRESH, data.refresh_token)
  if (data.id_token) localStorage.setItem(LS_ID, data.id_token)
  const expiresAt = Date.now() + (data.expires_in ?? 300) * 1000
  localStorage.setItem(LS_EXPIRES, String(expiresAt))
}

export function clearTokens() {
  ;[LS_ACCESS, LS_REFRESH, LS_ID, LS_EXPIRES].forEach((k) => localStorage.removeItem(k))
}

// ---------- flow ----------

/** Redirect the browser to the Keycloak login page. */
export async function login() {
  const verifier = randomString()
  const state = randomString(24)
  sessionStorage.setItem(SS_VERIFIER, verifier)
  sessionStorage.setItem(SS_STATE, state)

  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: redirectUri(),
    response_type: 'code',
    scope: 'openid profile email',
    state,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: 'S256',
  })
  window.location.assign(`${AUTH_URL}?${params.toString()}`)
}

/** Handle the redirect back from Keycloak. Returns true on success. */
export async function handleCallback(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  const state = params.get('state')
  const expectedState = sessionStorage.getItem(SS_STATE)
  const verifier = sessionStorage.getItem(SS_VERIFIER)

  sessionStorage.removeItem(SS_STATE)
  sessionStorage.removeItem(SS_VERIFIER)

  if (!code || !state || !expectedState || state !== expectedState || !verifier) {
    return false
  }

  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      redirect_uri: redirectUri(),
      code,
      code_verifier: verifier,
    }),
  })
  if (!res.ok) return false

  storeTokens(await res.json())
  return true
}

async function refresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem(LS_REFRESH)
  if (!refreshToken) return null
  const res = await fetch(TOKEN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: CLIENT_ID,
      refresh_token: refreshToken,
    }),
  })
  if (!res.ok) {
    clearTokens()
    return null
  }
  storeTokens(await res.json())
  return localStorage.getItem(LS_ACCESS)
}

/** Returns a usable access token, refreshing it first when expired. */
export async function getValidAccessToken(): Promise<string | null> {
  const token = localStorage.getItem(LS_ACCESS)
  if (!token) return null
  const expiresAt = Number(localStorage.getItem(LS_EXPIRES) || 0)
  if (Date.now() < expiresAt - 15_000) return token
  return refresh()
}

/** Current user from the access token claims. */
export function getUser(): User | null {
  const token = localStorage.getItem(LS_ACCESS)
  if (!token) return null
  const claims = decodeJwt(token)
  if (!claims) return null

  const name = (claims.name as string) || (claims.preferred_username as string) || 'User'
  const email = (claims.email as string) || ''
  const roles =
    ((claims.realm_access as { roles?: string[] })?.roles || []).filter((r) =>
      ['admin', 'hr', 'employee'].includes(r),
    )
  const parts = name.trim().split(/\s+/)
  const initials = (parts.length > 1
    ? parts[0][0] + parts[parts.length - 1][0]
    : name.slice(0, 2)
  ).toUpperCase()

  return {
    name,
    username: (claims.preferred_username as string) || '',
    email,
    roles,
    initials,
  }
}

/** Clear local session and redirect to Keycloak logout. */
export function logout() {
  const idToken = localStorage.getItem(LS_ID)
  clearTokens()
  const params = new URLSearchParams({
    post_logout_redirect_uri: window.location.origin,
    client_id: CLIENT_ID,
  })
  if (idToken) params.set('id_token_hint', idToken)
  window.location.assign(`${LOGOUT_URL}?${params.toString()}`)
}

/** True when the current user holds any of the given realm roles. */
export function hasAnyRole(...roles: string[]): boolean {
  const user = getUser()
  return !!user && roles.some((r) => user.roles.includes(r))
}

/** Matches the API RolesGuard policy matrix. */
export const canManageEmployees = () => hasAnyRole('admin', 'hr')
export const canDeleteEmployees = () => hasAnyRole('admin')
export const canDeleteDocuments = () => hasAnyRole('admin', 'hr')
export const canManageOrgUnits = () => hasAnyRole('admin', 'hr')
export const canDeleteOrgUnits = () => hasAnyRole('admin')
export const canDecideLeave = () => hasAnyRole('admin', 'hr')
